/**
 * VersonaTravelModule — renders Versonas that have BOARDED A CRAFT to cross the
 * Commons quickly (the travel wave). The server's `versona_journey` rows are the
 * authority — one per Versona in transit — carrying the craft kind (air ship /
 * land hovercraft), its chassis GLB, the authoritative position, and (for air)
 * the cruise altitude. This module turns each row into a moving craft in the
 * SAME scene as the Commons ground, so you see them WHETHER you walk below or
 * fly the atmosphere overhead: ships crossing the sky toward a distant town, a
 * hovercraft skimming the plains.
 *
 * WHY its own render path (not the versona crowd): the crowd (EnvironmentModule)
 * only draws Versonas within the ~400 m AOI window, and a Versona in transit has
 * NO `versona_state` hot row at all (the server deletes it for the journey's
 * duration). Craft must be seen kilometres off, so they render from the few
 * world-scoped `versona_journey` rows with their own long draw distance and fog
 * masking, exactly like AtlasSkyTrafficModule's cosmetic flybys — but these are
 * REAL, server-authoritative journeys.
 *
 * Rebase-safe by the CPU path: a plain GLB clone added to app.world.scene with
 * absolute coords has its huge translation cancelled in f64 by the origin-rebase
 * scene offset before the f32 upload (only INSTANCED pools need an anchor group).
 */
import * as THREE from 'three/webgpu';
import { disposeLater } from '../lib/gpu_dispose';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { reportCraft } from '../lib/audio/craft_feed.js';
import { makeSystem, Phase } from '../engine-compat.js';
import type { AssetModule } from './AssetModule.js';
import { prefabAssetId } from '../lib/player_models.js';
import { groundYIn } from '../lib/grounding.js';
import { planFor, planKeyOf, progressAt, advanceFlight, flightAt, ROLL_RATE_DEG_S, type FlightPlan, type FlightPose } from '../lib/flight_profile.js';
import { markShared } from '../lib/defer_dispose.js';
import { STDB_WS_URI, STDB_DB_NAME, TOKEN_KEY } from '../config.js';

/** civa_ship covers any air chassis whose fleet GLB has not resolved yet. */
const AIR_FALLBACK_PREFAB = 'structure:civa_ship';
const SHIP_FIT_M = 15;      // box-fit length for an air hull
const HOVER_FIT_M = 8.5;    // box-fit length for a land hull
const VIS_RANGE_M = 4600;   // craft farther than this from the player are not built (fog covers them)
const HOVER_CLEAR_M = 1.1;  // land craft skims this high above the ground
const SNAP_ERR_M = 140;     // horizontal correction beyond this = a fresh craft / teleport, snap

interface JourneyRowLike {
  versonaId: bigint; worldId: bigint; kind: string; chassisKey: string;
  // from* and cruiseAlt are read so the client can rebuild the SERVER'S flight
  // plan (lib/flight_profile.ts) rather than extrapolating a straight line.
  fromX: number; fromZ: number;
  curX: number; curZ: number; destX: number; destZ: number;
  cruiseAlt: number; altitude: number; speed: number; yaw: number;
}

interface Craft {
  kind: string;
  chassisKey: string;
  group: THREE.Object3D;     // the live scene node (placeholder until the GLB resolves)
  usingGlb: boolean;
  x: number; z: number;      // rendered horizontal position
  y: number;                 // rendered world-Y
  yaw: number;
  bank: number;
  // Smooth motion from sparse (~1.2 s) HTTP samples: extrapolate at constant
  // velocity from the latest sample, then decay a per-sample offset so a new
  // sample reconciles seamlessly instead of snapping (fixes the hold-then-jump).
  baseX: number; baseZ: number; baseAlt: number; baseT: number; // latest server sample + arrival time (-1 = uninit)
  baseS: number;                                                 // arc length along the plan at that sample
  plan: FlightPlan | null;                                       // the leg's flight plan (rebuilt when the leg changes)
  planKey: string;
  pitch: number;                                                 // nose up/down, from the climb gradient
  pose: FlightPose | null;
  offX: number; offZ: number; offAlt: number;                    // continuity offset, decays to 0
  renderAlt: number;                                             // last rendered altitude (for offset continuity)
  gx?: number; gz?: number; ground?: number;                     // last analytic ground sample (resampled after 8 m)
}

export class VersonaTravelModule implements IModule {
  readonly name = 'VersonaTravel';

  private app: App | null = null;
  private readonly crafts = new Map<string, Craft>();
  private readonly masters = new Map<string, THREE.Object3D>(); // chassisKey → box-fit master
  private readonly loading = new Set<string>();
  private frames = 0;      // diag: frame() invocations
  private builtEver = 0;   // diag: total craft spawned
  private serverRows: JourneyRowLike[] = []; // journeys from the HTTP SQL poll (the WS SDK doesn't apply live updates for this table)
  private pollAt = 0;      // last poll wall-clock
  private polling = false; // one poll in flight
  private polls = 0;       // diag: completed polls
  private pollErr = '';    // diag: last poll error

  constructor(private readonly assets: AssetModule) {}

  init(app: App): void {
    this.app = app;
    // Animation/39: per-frame interpolation, right after the versona crowd (Animation/38).
    app.world.addSystem(makeSystem('VersonaTravel', Phase.Animation, 39, (dt) => this.frame(dt)));
    (window as unknown as { __versonaTravel?: unknown }).__versonaTravel = {
      state: () => this.probeState(),
      craft: (id: string) => { const c = this.crafts.get(id); return c ? { x: c.x, z: c.z, y: c.y } : null; },
      // Deterministic interpolation proof (headless can't measure per-frame
      // smoothness): drive advance() with SYNTHETIC time steps on a fixed
      // journey and return the rendered x at each. The fix (constant-velocity
      // extrapolation) yields uniformly-spaced x; the old hold-then-jump did not.
      simSmooth: () => this.simSmooth(),
    };
  }

  private active(): boolean {
    // The Commons ground game — shown both on foot and in atmospheric flight,
    // hidden only when the player has left for the separate space world.
    return atlas.currentWorldId !== null
      && atlas.currentWorldId === atlas.commonsWorldId
      && !atlas.spaceFlight;
  }

  private frame(dt: number): void {
    this.frames++;
    if (!this.app) return;
    const scene = this.app.world.scene;
    if (!this.active()) { this.clear(scene); return; }
    const conn = atlas.conn;
    if (!conn) return;
    this.schedulePoll();

    const rows = this.serverRows;
    const px = atlas.localX, pz = atlas.localZ;
    const live = new Set<string>();

    for (const j of rows) {
      if (j.worldId !== atlas.commonsWorldId) continue;
      if (Math.hypot(j.curX - px, j.curZ - pz) > VIS_RANGE_M) continue;
      const id = String(j.versonaId);
      live.add(id);
      let c = this.crafts.get(id);
      if (!c) { c = this.spawn(scene, j); this.crafts.set(id, c); }
      this.advance(conn, c, j, dt);
    }

    // Reap craft whose journey ended (arrived) or drifted out of range.
    for (const [id, c] of this.crafts) {
      if (!live.has(id)) { scene.remove(c.group); this.crafts.delete(id); }
    }
  }

  /**
   * Refresh the journey list over the HTTP SQL endpoint every ~1.2 s. The
   * SpacetimeDB JS SDK snapshots versona_journey but does NOT apply the table's
   * live INCREMENTAL updates (proven: the WS server stream carries them, the CLI
   * subscribe sees them, but conn.db.versonaJourney froze) — so, like the Versona
   * Universe dossier, we poll the read-only SQL endpoint and dead-reckon between
   * samples. Journeys are few (tens), the Commons only. Async + single-flight.
   */
  private schedulePoll(): void {
    const now = performance.now();
    if (this.polling || now - this.pollAt < 1200) return;
    this.polling = true;
    this.pollAt = now;
    void this.sqlJourneys()
      .then((rows) => { this.serverRows = rows; this.polls++; this.pollErr = ''; })
      .catch((e) => { this.pollErr = String(e).slice(0, 90); })
      .finally(() => { this.polling = false; });
  }

  private async sqlJourneys(): Promise<JourneyRowLike[]> {
    const wid = atlas.commonsWorldId;
    if (wid == null) return this.serverRows;
    const base = STDB_WS_URI.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
    const token = (atlas.conn as unknown as { token?: string } | null)?.token ?? localStorage.getItem(TOKEN_KEY) ?? '';
    const res = await fetch(`${base}/v1/database/${STDB_DB_NAME}/sql`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/plain' },
      body: `SELECT * FROM versona_journey WHERE world_id = ${wid}`,
    });
    if (!res.ok) throw new Error(`sql ${res.status}`);
    const out = (await res.json()) as Array<{ schema: { elements: Array<{ name: { some?: string } }> }; rows: unknown[][] }>;
    const stmt = out[0];
    if (!stmt) return [];
    const cols = stmt.schema.elements.map((e) => e.name.some ?? '');
    const at = (n: string): number => cols.indexOf(n);
    const iId = at('versona_id'), iWid = at('world_id'), iKind = at('kind'), iCh = at('chassis_key');
    const iFx = at('from_x'), iFz = at('from_z');
    const iCx = at('cur_x'), iCz = at('cur_z'), iDx = at('dest_x'), iDz = at('dest_z');
    const iCru = at('cruise_alt'), iAlt = at('altitude'), iSpd = at('speed'), iYaw = at('yaw');
    const bi = (v: unknown): bigint => BigInt(v as string | number);
    const nu = (v: unknown): number => Number(v ?? 0);
    return stmt.rows.map((r) => ({
      versonaId: bi(r[iId]), worldId: bi(r[iWid]),
      kind: String(r[iKind] ?? ''), chassisKey: String(r[iCh] ?? ''),
      fromX: nu(r[iFx]), fromZ: nu(r[iFz]),
      curX: nu(r[iCx]), curZ: nu(r[iCz]), destX: nu(r[iDx]), destZ: nu(r[iDz]),
      cruiseAlt: nu(r[iCru]), altitude: nu(r[iAlt]), speed: nu(r[iSpd]), yaw: nu(r[iYaw]),
    }));
  }

  private spawn(scene: THREE.Object3D, j: JourneyRowLike): Craft {
    const c: Craft = {
      kind: j.kind, chassisKey: j.chassisKey,
      group: this.placeholder(j.kind), usingGlb: false,
      x: j.curX, z: j.curZ, y: 0, yaw: j.yaw, bank: 0,
      baseX: j.curX, baseZ: j.curZ, baseAlt: j.altitude, baseT: -1, baseS: 0, plan: null, planKey: '', pitch: 0, pose: null,
      offX: 0, offZ: 0, offAlt: 0, renderAlt: 0,
    };
    scene.add(c.group);
    this.builtEver++;
    this.ensureMaster(j.kind, j.chassisKey);
    return c;
  }

  private advance(conn: unknown, c: Craft, j: JourneyRowLike, dt: number, now: number = performance.now()): void {
    const step = Math.max(0, Math.min(0.1, dt)); // guard a long stalled frame

    // THE SAME FLIGHT PLAN THE SERVER FLEW (2026-09-20). `flight_profile.ts` is
    // a byte-identical mirror, and the plan is a pure function of columns the
    // row already carries, so this walk cannot disagree with the server's — it
    // is the same curve, the same speed profile, the same vertical profile. The
    // craft used to extrapolate a straight line at a constant `j.speed`, which
    // fought the server the moment either curved or changed pace.
    // `planKeyOf` is the mirror's own definition of "this is a different
    // flight" — using it here means the client cannot disagree with the server
    // about when a leg changed (the local key omitted cruiseAlt and kind).
    const key = planKeyOf(j);
    if (!c.plan || c.planKey !== key) {
      c.plan = planFor(j);
      c.planKey = key;
      c.baseT = -1;   // rebase onto the new leg before extrapolating again
    }
    const plan = c.plan;

    // A NEW server sample arrived (the ~1.2 s HTTP poll refreshed curX/curZ)?
    // Rebase onto it, carrying the current extrapolated error as an offset that
    // decays away — so the craft NEVER holds-then-jumps; it glides continuously.
    if (c.baseT < 0 || Math.abs(j.curX - c.baseX) > 1e-3 || Math.abs(j.curZ - c.baseZ) > 1e-3) {
      if (c.baseT >= 0) {
        const predicted = flightAt(plan, advanceFlight(plan, c.baseS, (now - c.baseT) / 1000));
        c.offX = predicted.x - j.curX;
        c.offZ = predicted.z - j.curZ;
        c.offAlt = c.renderAlt - j.altitude;
        if (Math.hypot(c.offX, c.offZ) > SNAP_ERR_M) { c.offX = 0; c.offZ = 0; c.offAlt = 0; } // teleport → snap
      }
      c.baseX = j.curX; c.baseZ = j.curZ; c.baseAlt = j.altitude; c.baseT = now;
      c.baseS = progressAt(plan, j.curX, j.curZ);
    }
    // Decay the reconciliation offset toward 0 (~0.3 s time constant).
    const decay = Math.exp(-step / 0.3);
    c.offX *= decay; c.offZ *= decay; c.offAlt *= decay;
    // Walk the plan forward from the latest sample. `advanceFlight` integrates
    // the speed profile, so the craft accelerates out and settles in exactly as
    // the server does; the plan's own end caps it, so a stale row (the ~1.2 s
    // window after an arrived journey is deleted) never skates past.
    const el = (now - c.baseT) / 1000;
    const pose = flightAt(plan, advanceFlight(plan, c.baseS, el));
    c.pose = pose;
    c.x = pose.x + c.offX;
    c.z = pose.z + c.offZ;

    // Altitude: air rides the server's cruise-ramp above the terrain it is over;
    // land skims a fixed clearance above the ground.
    // The terrain under a craft is sampled analytically (it flies far outside
    // the cached chunk ring: 4 stampedHeight evaluations each) — resample after
    // 8 m of travel, not every frame (2026-09-12).
    if (c.ground === undefined || c.gx === undefined || c.gz === undefined || Math.hypot(c.x - c.gx, c.z - c.gz) > 8) { c.ground = groundYIn(conn as never, atlas.currentWorldId, c.x, c.z); c.gx = c.x; c.gz = c.z; }
    const ground = c.ground;
    // Altitude comes from the PLAN, not the last sample: the row is 1.2 s old,
    // so riding `baseAlt` made a descent a staircase of 1.2 s steps.
    c.renderAlt = j.kind === 'air' ? Math.max(6, pose.alt + c.offAlt) : HOVER_CLEAR_M;
    const targetY = ground + c.renderAlt;
    c.y += (targetY - c.y) * Math.min(1, step * 6);

    // Heading, bank and PITCH (hulls are authored nose −X — the sky-traffic convention).
    // The nose is the crabbed heading, not the track: a craft in a crosswind
    // does not point where it is going.
    // `c.yaw` is now the WORLD heading of the nose (the −X hull offset lives in
    // `orient`, not here), so it is also what `craftPose` reports.
    const targetYaw = pose.yaw;
    let dyaw = targetYaw - c.yaw;
    while (dyaw > Math.PI) dyaw -= Math.PI * 2;
    while (dyaw < -Math.PI) dyaw += Math.PI * 2;
    c.yaw += dyaw * Math.min(1, step * 4);
    // Bank is the turn the craft is actually flying — φ = atan(v²κ/g), capped at
    // the 25° airline comfort limit — and the roll into it is RATE-limited
    // (2–4 s to reach 30°), so it banks like an aircraft rather than snapping.
    const bankTarget = j.kind === 'air' ? pose.bank : 0;
    const rollMax = ROLL_RATE_DEG_S * (Math.PI / 180) * step;
    c.bank += Math.max(-rollMax, Math.min(rollMax, bankTarget - c.bank));
    // Pitch follows the climb/descent gradient; the hull used to stay dead level
    // through the whole climb, which is most of why it read as a sliding prop.
    const pitchTarget = j.kind === 'air' ? pose.pitch : 0;
    c.pitch += (pitchTarget - c.pitch) * Math.min(1, step * 2);

    // Upgrade the placeholder in place once the GLB master resolves.
    const master = this.masters.get(j.chassisKey);
    if (master && !c.usingGlb) {
      const glb = master.clone();
      c.group.parent?.add(glb);
      c.group.parent?.remove(c.group);
      c.group = glb;
      c.usingGlb = true;
    }

    c.group.position.set(c.x, c.y, c.z);
    reportCraft(`vt:${String(j.versonaId)}`, j.kind === 'air' ? 'ship' : 'hover', c.x, c.y, c.z, now); // heard as it passes (lib/audio/craft_feed.ts)
    // A HULL FLIES NOSE-FIRST: build the basis from a forward vector that
    // already carries the climb angle, then roll about the fuselage — the
    // WarAirModule idiom (its `orient`). Euler angles cannot express this for a
    // nose −X hull without picking the axes by trial.
    this.orient(c.group, c.yaw, j.kind === 'air' ? c.pitch : 0, j.kind === 'air' ? c.bank : 0);
  }

  private readonly _f = new THREE.Vector3();
  private readonly _bx = new THREE.Vector3();
  private readonly _by = new THREE.Vector3();
  private readonly _bz = new THREE.Vector3();
  private readonly _m = new THREE.Matrix4();

  /**
   * Seat a hull's basis: local +X = −forward (the nose is −X), +Y = world up
   * orthogonalised against the forward so PITCH FOLLOWS THE CLIMB, +Z = X × Y;
   * then bank about the fuselage axis. The same helper WarAirModule uses.
   */
  private orient(obj: THREE.Object3D, yaw: number, pitch: number, bank: number): void {
    const cp = Math.cos(pitch);
    const f = this._f.set(Math.sin(yaw) * cp, Math.sin(pitch), Math.cos(yaw) * cp).normalize();
    const X = this._bx.copy(f).negate();
    const Y = this._by.set(0, 1, 0);
    Y.addScaledVector(f, -Y.dot(f));
    if (Y.lengthSq() < 1e-6) Y.set(0, 1, 0); else Y.normalize();
    const Z = this._bz.crossVectors(X, Y);
    this._m.makeBasis(X, Y, Z);
    obj.quaternion.setFromRotationMatrix(this._m);
    obj.rotateX(bank);
  }

  /** Load + box-fit the chassis master once; clones ride each craft. */
  private ensureMaster(kind: string, chassisKey: string): void {
    if (this.masters.has(chassisKey) || this.loading.has(chassisKey)) return;
    const conn = atlas.conn;
    if (!conn) return;
    let id = prefabAssetId(conn, 'prop:' + chassisKey);
    if (id === null && kind === 'air') id = prefabAssetId(conn, AIR_FALLBACK_PREFAB);
    if (id === null || id === 0n) return; // catalog not loaded yet — retry next frame
    this.loading.add(chassisKey);
    this.assets.load(id)
      .then((master) => {
        // A PRIVATE clone: `assets.load` returns the shared cache master, and
        // scaling it in place shrank every other user of the same hull key
        // (SpaceModule / CargoSky measure the master before fitting — the
        // tiny-ship-in-space class, 2026-08-20; 2026-09-12).
        const obj = master.clone();
        const fit = kind === 'air' ? SHIP_FIT_M : HOVER_FIT_M;
        const box = new THREE.Box3().setFromObject(obj);
        const size = new THREE.Vector3(); box.getSize(size);
        const longest = Math.max(size.x, size.y, size.z);
        if (longest > 1e-3) obj.scale.multiplyScalar(fit / longest);
        markShared(obj); // clones tear down freely; the master's buffers survive
        this.masters.set(chassisKey, obj);
      })
      .catch(() => { /* placeholder stays until a later window resolves it */ })
      .finally(() => { this.loading.delete(chassisKey); });
  }

  /** Shared placeholder parts — ONE material/geometry set for every craft
   *  (bug sweep: per-craft `new MeshStandardMaterial` meant a synchronous
   *  pipeline build per spawn and a VRAM leak on every teardown path). */
  private phParts: { g: THREE.BufferGeometry[]; m: THREE.MeshStandardMaterial[] } | null = null;
  private phPart(i: number): { hull: THREE.BufferGeometry; mat: THREE.MeshStandardMaterial } {
    if (!this.phParts) {
      this.phParts = {
        g: [
          new THREE.CapsuleGeometry(1.5, 9, 4, 8).rotateZ(Math.PI / 2),
          new THREE.BoxGeometry(3.2, 0.3, 6),
          new THREE.BoxGeometry(6, 0.7, 3),
          new THREE.CapsuleGeometry(0.35, 0.8, 3, 6),
        ],
        m: [
          new THREE.MeshStandardMaterial({ color: 0x8fa9c8, metalness: 0.6, roughness: 0.35, emissive: 0x14202f, emissiveIntensity: 0.5 }),
          new THREE.MeshStandardMaterial({ color: 0x6b7f9c, metalness: 0.5, roughness: 0.5 }),
          new THREE.MeshStandardMaterial({ color: 0x9a8f6b, metalness: 0.35, roughness: 0.6 }),
          new THREE.MeshStandardMaterial({ color: 0x3a4a63, roughness: 0.8 }),
        ],
      };
    }
    return { hull: this.phParts.g[i], mat: this.phParts.m[i] };
  }

  /** A stylised primitive shown instantly (and if the GLB never resolves). */
  private placeholder(kind: string): THREE.Object3D {
    const g = new THREE.Group();
    if (kind === 'air') {
      const p0 = this.phPart(0), p1 = this.phPart(1);
      const hull = new THREE.Mesh(p0.hull, p0.mat);
      const wing = new THREE.Mesh(p1.hull, p1.mat);
      wing.position.y = -0.2;
      g.add(hull, wing);
      g.scale.setScalar(1.6);
    } else {
      const p2 = this.phPart(2), p3 = this.phPart(3);
      const deck = new THREE.Mesh(p2.hull, p2.mat);
      deck.position.y = 0.5;
      const rider = new THREE.Mesh(p3.hull, p3.mat);
      rider.position.set(-1.2, 1.5, 0);
      g.add(deck, rider);
    }
    g.name = 'versona-craft';
    return g;
  }

  private clear(scene: THREE.Object3D): void {
    if (this.crafts.size === 0) return;
    for (const c of this.crafts.values()) scene.remove(c.group);
    this.crafts.clear();
  }

  /** (bug sweep) the module never had a dispose — crafts, masters and the
   *  shared placeholder parts all survived teardown. */
  /** Rendered pose of a versona's craft while it travels — WarAir escorts
   *  formate on the real march instead of an abstract racetrack. */
  craftPose(versonaId: bigint): { x: number; y: number; z: number; yaw: number } | null {
    const c = this.crafts.get(versonaId.toString());
    return c ? { x: c.x, y: c.y, z: c.z, yaw: c.yaw } : null;
  }

  dispose(): void {
    for (const c of this.crafts.values()) c.group.parent?.remove(c.group);
    this.crafts.clear();
    for (const m of this.masters.values()) m.parent?.remove(m);
    this.masters.clear();
    if (this.phParts) {
      for (const g of this.phParts.g) disposeLater(g);
      for (const m of this.phParts.m) disposeLater(m);
      this.phParts = null;
    }
    delete (globalThis as Record<string, unknown>).__versonaTravel;
  }

  private simSmooth(): { steps: number[]; reconcileJump: number } {
    const speed = 100, yaw = Math.PI / 2; // heading +x (sin=1)
    // MID-CRUISE on purpose: the craft now accelerates out of A and settles into
    // B (flight_profile.ts), so "uniform steps" is only the contract where the
    // speed profile is flat. The reconcile-without-a-jump proof is unchanged,
    // and the profile itself is proven purely by `verify:flight-profile`.
    const j: JourneyRowLike = {
      versonaId: 0n, worldId: atlas.commonsWorldId ?? 0n, kind: 'air', chassisKey: '__sim',
      fromX: 0, fromZ: 0, curX: 20_000, curZ: 0, destX: 1_000_000, destZ: 0,
      cruiseAlt: 300, altitude: 300, speed, yaw,
    };
    const c: Craft = {
      kind: 'air', chassisKey: '__sim', group: new THREE.Group(), usingGlb: true,
      x: 0, z: 0, y: 0, yaw: 0, bank: 0,
      baseX: 0, baseZ: 0, baseAlt: 0, baseT: -1, baseS: 0, plan: null, planKey: '', pitch: 0, pose: null, offX: 0, offZ: 0, offAlt: 0, renderAlt: 0,
    };
    const conn = atlas.conn;
    const steps: number[] = [];
    // Constant-velocity phase: same journey sample, time advances 0.25 s/step →
    // x MUST advance a uniform speed·0.25 = 25 m each step (the smoothness proof).
    for (let t = 0; t <= 2000; t += 250) { this.advance(conn, c, j, 0.25, t); steps.push(Math.round(c.x * 100) / 100); }
    // Reconcile phase: a new server sample lands 40 m behind the extrapolation.
    // The offset must absorb it — the rendered x may not JUMP.
    const before = c.x;
    const j2 = { ...j, curX: c.x - 40, curZ: 0 }; // server says we're 40 m back
    this.advance(conn, c, j2, 0.016, 2016);
    const reconcileJump = Math.abs(c.x - before);
    return { steps, reconcileJump: Math.round(reconcileJump * 100) / 100 };
  }

  private probeState(): unknown {
    const journeys = this.serverRows.filter((j) => j.worldId === atlas.commonsWorldId);
    const air = journeys.filter((j) => j.kind === 'air').length;
    const crafts = [...this.crafts.values()].map((c) => ({
      kind: c.kind, chassis: c.chassisKey, glb: c.usingGlb,
      x: Math.round(c.x), z: Math.round(c.z), y: Math.round(c.y), alt: Math.round(c.y),
    }));
    let nearest = Infinity;
    const px = atlas.localX, pz = atlas.localZ;
    for (const j of journeys) nearest = Math.min(nearest, Math.hypot(j.curX - px, j.curZ - pz));
    return {
      frames: this.frames,
      builtEver: this.builtEver,
      polls: this.polls,
      pollErr: this.pollErr,
      serverRows: this.serverRows.length,
      player: { x: Math.round(px), z: Math.round(pz) },
      nearestJourneyM: Number.isFinite(nearest) ? Math.round(nearest) : -1,
      active: this.active(),
      journeys: journeys.length,
      airJourneys: air,
      landJourneys: journeys.length - air,
      crafts: crafts.length,
      maxAltitude: crafts.reduce((m, c) => Math.max(m, c.y), 0),
      sample: crafts.slice(0, 6),
      // A STABLE mid-flight craft (the one farthest from its destination) for the
      // SMOOTHNESS probe — track it by id via craft(id); the fix gives smooth
      // constant-velocity motion (x = base + speed·elapsed), not hold-then-jump.
      bestCraft: (() => {
        let best = null, bestRem = -1;
        for (const [id, c] of this.crafts) {
          const j = this.serverRows.find((r) => String(r.versonaId) === id);
          const rem = j ? Math.hypot(j.destX - c.x, j.destZ - c.z) : 0;
          if (rem > bestRem) { bestRem = rem; best = { id, x: c.x, z: c.z, kind: c.kind, rem: Math.round(rem) }; }
        }
        return best;
      })(),
    };
  }
}
